import { Component, OnInit, Inject, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MessageService } from 'primeng/api';
import { first } from 'rxjs/operators';
import { TrackedLinkService } from '@app/_components/tracked-link';
import { MsgTemplateService } from '@app/_components/msg-template/msg-template.service';
import { ReservationService } from '@app/_components/reservation/reservation.service';
import { MsgService } from '@app/_components/msg';
import { InboxComponent } from '@app/_components/inbox/inbox/inbox.component';

@Component({
	standalone: false,
	selector: 'app-template-analytics',
	templateUrl: './template-analytics.component.html',
	styleUrls: ['./template-analytics.component.css'],
	providers: [MessageService]
})
export class TemplateAnalyticsComponent implements OnInit {

	msgTemplates: any[] = [];
	tplId: number = null;
	tplDateFrom: string = new Date().toISOString().substring(0, 10);
	tplDateTo: string = new Date().toISOString().substring(0, 10);
	tplStats: any = null;
	loading = false;
	templatesLoading = true;

	// Recipient detail
	recipients: any[] = [];
	recipientLoading = false;
	recipientType: string = null; // 'opened' or 'clicked'
	recipientPage: number = 0;
	recipientPageSize: number = 25;

	// Recipient table sorting
	recipientSortField: string = null;
	recipientSortDir: number = 1; // 1 = asc, -1 = desc

	// URL breakdown
	urlClicks: any[] = [];
	urlLoading = false;

	// Topic map
	topicMap: { [key: number]: string } = {
		100: 'Reservation', 200: 'Campaign', 300: 'IMAP Read', 400: 'Jobs',
		500: 'Owner Profile', 600: 'Task', 700: 'Proforma', 800: 'Website Query',
		900: 'Custom Mail', 1000: 'Public Review', 1100: 'Sent Review Request',
		1200: 'Survey Guest Exit', 1300: 'Created Lead Profile', 1600: 'SMS Received',
		1700: 'Send SMS', 1800: 'Review Notification'
	};

	// Track loading state per reservationId for open order
	openingOrder: { [key: number]: boolean } = {};
	isPage = false;

	constructor(
		private trackedLinkService: TrackedLinkService,
		private msgTemplateService: MsgTemplateService,
		private reservationService: ReservationService,
		private messageService: MessageService,
		private dialog: MatDialog,
		private msgService: MsgService,
		private route: ActivatedRoute,
		@Optional() @Inject(MAT_DIALOG_DATA) data: any
	) {
		if (data && data.templateId) {
			this.tplId = data.templateId;
		}
		if (data && data.dateFrom !== undefined) {
			this.tplDateFrom = data.dateFrom;
		}
		if (data && data.dateTo !== undefined) {
			this.tplDateTo = data.dateTo;
		}
		this.isPage = !data;
	}

	ngOnInit() {
		if (this.isPage) {
			const urlParams = new URLSearchParams(window.location.search);
			const templateId = urlParams.get('templateId');
			if (templateId) { this.tplId = +templateId; }
			const dateFrom = urlParams.get('dateFrom');
			if (dateFrom !== null) { this.tplDateFrom = dateFrom; }
			const dateTo = urlParams.get('dateTo');
			if (dateTo !== null) { this.tplDateTo = dateTo; }
		}
		this.loadTemplates();
	}

	loadTemplates() {
		this.templatesLoading = true;
		this.msgTemplateService.findAllWithoutPage().pipe(first()).subscribe(
			(res: any) => {
				this.msgTemplates = (res.data.msgTemplates || []).map((t: any) => ({
					...t,
					displayLabel: t.msgTemplateId + ' - ' + (t.name || '')
				}));
				// Auto-search if templateId was pre-selected
				if (this.tplId) { this.search(); }
			},
			(err: any) => { console.log('Template load error: ' + JSON.stringify(err)); },
			() => { this.templatesLoading = false; }
		);
	}

	search() {
		if (!this.tplId) { return; }
		this.loading = true;
		this.tplStats = null;
		this.recipients = [];
		this.recipientType = null;
		this.urlClicks = [];
		this.trackedLinkService.getTemplateAnalytics(
			this.tplId,
			this.tplDateFrom || undefined,
			this.tplDateTo || undefined
		).pipe(first()).subscribe(
			(res: any) => {
				this.tplStats = res.data.analytics;
				// Auto-load URL breakdown for templates
				this.loadUrlClicks();
			},
			(err: any) => {
				console.log('Template analytics error: ' + JSON.stringify(err));
				this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load template analytics.', life: 3000 });
			},
			() => { this.loading = false; }
		);
	}

	loadUrlClicks() {
		if (!this.tplId) { return; }
		this.urlLoading = true;
		this.urlClicks = [];
		this.trackedLinkService.getTemplateUrlClicks(
			this.tplId, this.tplDateFrom || undefined, this.tplDateTo || undefined
		).pipe(first()).subscribe(
			(res: any) => { this.urlClicks = res.data.urlClicks || []; },
			(err: any) => { console.log('Template URL clicks error: ' + JSON.stringify(err)); },
			() => { this.urlLoading = false; }
		);
	}

	loadRecipients(type: string) {
		if (!this.tplId) { return; }
		this.recipientType = type;
		this.recipientLoading = true;
		this.recipients = [];
		this.recipientPage = 0;

		let obs;
		if (type === 'all') {
			obs = this.trackedLinkService.getTemplateAllRecipients(this.tplId, this.tplDateFrom || undefined, this.tplDateTo || undefined);
		} else if (type === 'opened') {
			obs = this.trackedLinkService.getTemplateOpenedRecipients(this.tplId, this.tplDateFrom || undefined, this.tplDateTo || undefined);
		} else {
			obs = this.trackedLinkService.getTemplateClickedRecipients(this.tplId, this.tplDateFrom || undefined, this.tplDateTo || undefined);
		}

		obs.pipe(first()).subscribe(
			(res: any) => { this.recipients = res.data.recipients || []; },
			(err: any) => {
				console.log('Recipient load error: ' + JSON.stringify(err));
				this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load recipients.', life: 3000 });
			},
			() => { this.recipientLoading = false; }
		);
	}

	sortRecipients(field: string) {
		if (this.recipientSortField === field) {
			this.recipientSortDir = this.recipientSortDir * -1;
		} else {
			this.recipientSortField = field;
			this.recipientSortDir = 1;
		}
		this.recipients = [...this.recipients].sort((a, b) => {
			const aVal = a[field] ?? '';
			const bVal = b[field] ?? '';
			if (aVal < bVal) { return -1 * this.recipientSortDir; }
			if (aVal > bVal) { return 1 * this.recipientSortDir; }
			return 0;
		});
		this.recipientPage = 0;
	}

	getSortIcon(field: string): string {
		if (this.recipientSortField !== field) { return 'pi pi-sort-alt'; }
		return this.recipientSortDir === 1 ? 'pi pi-sort-amount-up-alt' : 'pi pi-sort-amount-down-alt';
	}

	get pagedRecipients(): any[] {
		const start = this.recipientPage * this.recipientPageSize;
		return this.recipients.slice(start, start + this.recipientPageSize);
	}

	onRecipientPageChange(event: any) {
		this.recipientPage = event.page;
		this.recipientPageSize = event.rows;
	}

	openMessage(msgId: number) {
		if (!msgId) { return; }
		this.msgService.findById(msgId).pipe(first()).subscribe(
			(res: any) => {
				const msg = res.data.msg;
				if (!msg) {
					this.messageService.add({ severity: 'warn', summary: 'Not Found', detail: 'Message ' + msgId + ' not found.', life: 3000 });
					return;
				}
				const dialogConfig = new MatDialogConfig();
				dialogConfig.width = '100%';
				dialogConfig.height = '100%';
				dialogConfig.maxWidth = '1600px';
				dialogConfig.maxHeight = '800px';
				dialogConfig.disableClose = false;
				dialogConfig.autoFocus = true;
				dialogConfig.hasBackdrop = true;
				dialogConfig.data = { inbox: msg, title: 'View Message' };
				this.dialog.open(InboxComponent, dialogConfig);
			},
			(err: any) => {
				this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load message ' + msgId, life: 3000 });
			}
		);
	}

	openOrder(reservationId: number) {
		if (!reservationId || this.openingOrder[reservationId]) { return; }
		this.openingOrder[reservationId] = true;
		this.reservationService.getOrderIdByReservationId(reservationId).pipe(first()).subscribe(
			(res: any) => {
				window.open(`https://admin.kbmhawaii.com/order/${res?.data?.orderId}`, '_blank');
				this.openingOrder[reservationId] = false;
			},
			() => {
				this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to fetch order for reservation ' + reservationId, life: 3000 });
				this.openingOrder[reservationId] = false;
			}
		);
	}

	getTopicName(topic: number): string {
		if (!topic) return '';
		return this.topicMap[topic] || String(topic);
	}

	get openRate(): number {
		if (!this.tplStats || !this.tplStats.totalSent || this.tplStats.totalSent === 0) { return 0; }
		return (this.tplStats.totalOpened / this.tplStats.totalSent) * 100;
	}

	get clickRate(): number {
		if (!this.tplStats || !this.tplStats.totalSent || this.tplStats.totalSent === 0) { return 0; }
		return (this.tplStats.totalClicked_Recipients / this.tplStats.totalSent) * 100;
	}

	openInNewTab() {
		const params = new URLSearchParams();
		if (this.tplId) { params.set('templateId', String(this.tplId)); }
		if (this.tplDateFrom) { params.set('dateFrom', this.tplDateFrom); }
		if (this.tplDateTo) { params.set('dateTo', this.tplDateTo); }
		window.open('/template-analytics?' + params.toString(), '_blank');
	}

	getSelectedTemplateName(): string {
		if (!this.tplId || !this.msgTemplates) { return ''; }
		const found = this.msgTemplates.find(t => t.msgTemplateId === this.tplId);
		return found ? found.name : '';
	}
}
